import React, { useState } from "react"
import { Button, Input } from "reactstrap"

// Matches the exact masked shapes auth_schema emits ("*", "**", "X**Y").
const isMasked = v => typeof v === "string" && /^(\*{1,2}|.\*\*.)$/.test(v)

// A secret field with a reveal (👁) toggle. Secrets are masked on read, so the
// masked placeholder can't just be un-hidden — revealing fetches the FULL real
// value on demand via `onReveal(revealPath)` (an admin-gated backend call). If
// the user has typed a new value, that is shown/saved as-is (no fetch).
const SecretInput = ({
  value,
  onChange,
  label = "secret",
  placeholder,
  revealPath,
  onReveal,
  multiline = false,
}) => {
  const [revealed, setRevealed] = useState(false)
  const [revealedValue, setRevealedValue] = useState(null)
  const [loading, setLoading] = useState(false)
  const [dirty, setDirty] = useState(false) // user has typed a new value

  // While revealed and untouched, show the fetched real value; otherwise show
  // the form value (masked placeholder when hidden, or the user's new input).
  const displayValue =
    revealed && !dirty && revealedValue != null ? revealedValue : value || ""

  const handleToggle = async () => {
    if (revealed) {
      setRevealed(false)
      return
    }
    if (!dirty && isMasked(value) && revealPath && onReveal) {
      setLoading(true)
      try {
        const real = await onReveal(revealPath)
        setRevealedValue(real == null ? "" : String(real))
      } catch (e) {
        console.error("Failed to reveal secret:", e)
        setRevealedValue("")
      } finally {
        setLoading(false)
      }
    }
    setRevealed(true)
  }

  const handleChange = e => {
    setDirty(true)
    onChange(e.target.value)
  }

  const toggleBtn = (
    <Button
      type="button"
      aria-label={revealed ? `Hide ${label}` : `Show ${label}`}
      disabled={loading}
      onClick={handleToggle}
      style={{
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        backgroundColor: "#f8f9fa",
        border: "1px solid #ced4da",
        color: "#495057",
      }}
    >
      {loading ? "…" : revealed ? "🙈" : "👁️"}
    </Button>
  )

  if (multiline) {
    return (
      <>
        <div className="d-flex">
          <Input
            type="textarea"
            rows="4"
            value={displayValue}
            onChange={handleChange}
            placeholder={placeholder || `Enter ${label.toLowerCase()}`}
            style={{
              fontFamily: "monospace",
              fontSize: "12px",
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            }}
          />
          {toggleBtn}
        </div>
      </>
    )
  }

  return (
    <div className="d-flex">
      <Input
        type={revealed ? "text" : "password"}
        value={displayValue}
        onChange={handleChange}
        placeholder={placeholder || `Enter ${label.toLowerCase()}`}
        style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
      />
      {toggleBtn}
    </div>
  )
}

export default SecretInput
